import React from "react";
import { Grid, Message, Confirm, Button, Icon, Popup, Responsive, Label } from "semantic-ui-react";
import SidebarTitle from "components/Sidebar/SidebarTitle";
import { Field, Form, Formik } from "formik";
import CustomInputTextComponent from "components/Form/CustomInputTextComponent";
import SecondaryButton from "components/Buttons/SecondaryButton";
import MainButton from "components/Buttons/MainButton";
import InputPhone from "components/Form/InputPhone";
import moment from "moment";
import * as Yup from "yup";
import RadioButtons from "components/Form/RadioButtons";
import { searchPatientsMyLab, getMpiPatientByMatricule, getMatchingPatient, patchPatient, postPatient } from "services/patient";
import { isValidPhoneNumber, formatPhoneNumber } from "react-phone-number-input";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { searchInsurances } from "services/insurance";
import { getAllSpecialRegimes } from "services/specialRegime";
import { getAllProviders } from "services/provider";
import { searchDoctors } from "services/doctor";
import { searchPostalCodes } from "services/postalCode";
import { getStreetsByPostalCode } from "services/street";
import { getCitiesByPostalCode, resetCities, resetWorkCities } from "services/city";
import { connect } from "react-redux";
import Select from "components/Form/Select";
import AsyncFilterSelect from "components/Form/AsyncFilterSelect";
import debounce from "lodash.debounce";
import { ServiceEnum } from "config/service";
import { convertMyLabToPatient, convertForPatientMatcher } from "utils/patientConverter";
import { formatPhoneNumberToInternational, checkPrefixePhoneNumer, isSamePhoneNumber } from "utils/phone";
import { languagesOptions, countriesOptions, orderedSpecialRegimes, doctorHandleInputChange } from "utils/selectOptions";
import * as countries from "i18n-iso-countries";
import { toast } from "react-toastify";
import * as SpecialButtons from "components/Form/SpecialButtons";
import CustomDatePickerComponent from "components/Form/CustomDatePickerComponent";
import * as cdigit from "cdigit";
import FilePreview from "../FilePreview";
import InsuranceInput from "components/Form/InsuranceInput";
import { getAllContactComments } from "services/contactComment";
import { cleanPhoneNumber } from "utils/phone";
import * as langs from "@cospired/i18n-iso-languages";
import { capitalize, findNumberInAddress, removeNumberFromStreet } from "utils/patientConverter";
import { displayGenderText } from "utils/displayGender";
import GranteePatientModal from "features/FoldersOverview/components/modals/GranteePatientModal";
import IdentitoVigilanceEditPatientModal from "features/FolderManager/components/modals/IdentitoVigilanceEditPatientModal";
import { withTranslation } from "react-i18next";
import CompareMpiDataModal from "features/FolderManager/components/modals/CompareMpiDataModal";
import InsuranceModal from "../modals/InsuranceModal";

langs.registerLocale(require("@cospired/i18n-iso-languages/langs/fr.json"));
countries.registerLocale(require("i18n-iso-countries/langs/fr.json"));

const isMobile = () => {
	return window.innerWidth - Responsive.onlyMobile.maxWidth < 0;
};

const styles = {
	button: {
		backgroundColor: "#00c7b1",
		height: 40,
		width: 40,
		color: "#fff",
		fontFamily: "Lato",
		textTransform: "uppercase",
		padding: 0,
		fontSize: "0.8em",
		lineHeight: "40px",
		fontWeight: 600,
		cursor: "pointer",
		outline: "none",
		border: 0,
		borderRadius: "4px",
		boxShadow: "0 0 0 1px transparent inset, 0 0 0 0 rgba(34,36,38,.15) inset",
		transition: "opacity .1s ease,background-color .1s ease,color .1s ease,box-shadow .1s ease,background .1s ease",
		":hover": {
			backgroundColor: "#00bda8",
			boxShadow: "0 0 0 1px transparent inset, 0 0 0 0 rgba(34,36,38,.15) inset",
			color: "#f5f5f5",
		},
		":active": {
			backgroundColor: "#00ae99",
			color: "#e6e6e6",
		},
		":disabled": {
			backgroundColor: "#D3D3D3",
		},
	},
	icon: {
		margin: 0,
		color: "#fff",
	},
	rowWithoutPadding: {
		padding: !isMobile() ? 0 : "0 0 0 1em",
	},
};
const schemaValidation = (t) =>
	Yup.object().shape({
		gender: Yup.string().required(t("editPatient.requiredGender")),
		first_name: Yup.string().required(t("editPatient.requiredFirstName")),
		birth_name: Yup.string().required(t("editPatient.requiredBirthName")),
		married_name: Yup.string(),
		birth_date: Yup.date()
			.min(new Date("1900-01-01"), t("editPatient.birthMin"))
			.max(new Date(), t("editPatient.birthMax"))
			.required(t("editPatient.requiredBirth")),
		cns: Yup.string().when("ignoreCnsError", {
			is: false,
			then: Yup.string()
				.required(t("editPatient.requiredCns"))
				.test(
					"compareWithBirthDate",
					t("editPatient.birthNotEqualCns"),
					//Yup ref doesn't work with functional arrows
					function (value) {
						if (!value || value === undefined) return false;
						if (value?.length === 8 || value?.length === 13) {
							const ref = Yup.ref("birth_date");
							const birthDate = moment(this.resolve(ref)).format("YYYYMMDD");
							const birthDate2000 = moment(this.resolve(ref)).add(2000, "year").format("YYYYMMDD");
							const cnsBirthDate = value.substring(0, 8);
							return birthDate === cnsBirthDate || birthDate2000 === cnsBirthDate;
						}
						return true;
					}
				)
				.test("cnsLength", t("editPatient.cnsLength"), (value) => {
					if (!value || value === undefined) return false;
					return value?.length === 8 || value?.length === 13;
				})
				.test("cnsLength", t("editPatient.cnsLength"), (value) => {
					if (!value || value === undefined) return false;
					return value.match(new RegExp("^(([0-9]{13})|([0-9]{8}))"));
				})
				.test("cnsValidation", t("editPatient.invalidCns"), (value) => {
					if (!value || value === undefined) return false;

					if (value?.length === 8) return value.match(new RegExp(process.env.REACT_APP_NO_CNS_REGEX));

					if (value?.length === 13) {
						const stringToValidate = value.substring(0, 11);
						const luhnVal = value.charAt(11);
						const verhoeffVal = value.charAt(12);
						return (
							value.match(new RegExp(process.env.REACT_APP_CNS_REGEX)) &&
							cdigit.luhn.compute(stringToValidate) === luhnVal &&
							cdigit.verhoeff.compute(stringToValidate) === verhoeffVal
						);
					}

					return false;
				}),
		}),

		patientInsurances: Yup.array().of(
			Yup.object().shape({
				insurance: Yup.object().shape({ id: Yup.number() }).nullable(),
				number: Yup.string().nullable(),
			})
		),
		hasEmail: Yup.boolean(),
		email: Yup.string().email(t("editPatient.invalidEmail")),
		email_comment: Yup.string(),
		phone: Yup.string()
			.test("phoneTypeValidation", t("editPatient.notHomePhone"), (value) => {
				if (!value || value === "+352") return true;
				if (formatPhoneNumber(value) !== "") {
					const type = parsePhoneNumberFromString(value).getType();
					return type === "FIXED_LINE" || type === "FIXED_LINE_OR_MOBILE";
				} else if (value) {
					return false;
				}
				return true;
			})
			.test("phoneValidation", t("editPatient.unknownHomePhoneFormat"), (value) => {
				value = cleanPhoneNumber(value);
				return formatPhoneNumber(value) === "" || isValidPhoneNumber(value);
			}),
		phone_comment: Yup.string(),
		hasMobilePhone: Yup.boolean(),
		mobile_phone: Yup.string()
			.min(2, "Longueur min : 2 caractères")
			.max(50, "Longueur max : 50 caractères")
			.test("phoneTypeValidation", "Il ne s'agit pas d'un numéro de téléphone mobile", (value) => {
				if (!value || value === "+352") return true;
				if (formatPhoneNumber(value) !== "") {
					const type = parsePhoneNumberFromString(value).getType();
					return type === "MOBILE" || type === "FIXED_LINE_OR_MOBILE";
				}
			})
			.test("phoneValidation", "Le format du téléphone mobile est inconu", (value) => {
				if (!value || value === "+352") return true;
				return isValidPhoneNumber(cleanPhoneNumber(value));
			}),
		mobile_phone_comment: Yup.string(),
		mobile_phone_is_relative: Yup.boolean(),
		hasHomeAddress: Yup.boolean(),
		home_street: Yup.string().when("hasHomeAddress", {
			is: true,
			then: Yup.string().required("Rue du domicile requise"),
		}),
		home_number: Yup.string(),
		home_complement: Yup.string().max(48, "Le complément d'adresse du domicile ne peut contenir que 48 caractères maximum"),
		home_city: Yup.string().when("hasHomeAddress", {
			is: true,
			then: Yup.string().required("Ville du domicile requise"),
		}),
		home_postal_code: Yup.string().when("hasHomeAddress", {
			is: true,
			then: Yup.string().required("Code postal du domicile requis"),
		}),
		home_country: Yup.string().when("hasHomeAddress", {
			is: true,
			then: Yup.string().required("Pays du domicile requis"),
		}),
		hasWorkAddress: Yup.boolean(),
		work_street: Yup.string().when("hasWorkAddress", {
			is: true,
			then: Yup.string().required("Rue du lieu de travail requise"),
		}),
		work_number: Yup.string(),
		work_complement: Yup.string().max(48, "Le complément d'adresse du lieu de travail ne peut contenir que 48 caractères maximum"),
		work_city: Yup.string().when("hasWorkAddress", {
			is: true,
			then: Yup.string().required("Ville du lieu de travail requise"),
		}),
		work_postal_code: Yup.string().when("hasWorkAddress", {
			is: true,
			then: Yup.string().required("Code postal du lieu de travail requis"),
		}),
		work_country: Yup.string().when("hasWorkAddress", {
			is: true,
			then: Yup.string().required("Pays du lieu de travail requis"),
		}),
		work_company: Yup.string(),
		specialRegime: Yup.object().shape({
			id: Yup.number().required("Régime requis").moreThan(0, "Régime requis"),
		}),
		provider_id: Yup.number().nullable(),
		doctor: Yup.object().shape({ id: Yup.number() }).nullable(),
		languages: Yup.array().of(Yup.object()).nullable(),
	});

//Wrapper used to prevent to many re-render of FilePreview iframe
const EditPatient = (props) => {
	let scanList = [];
	if (props.prescriptions && props.prescriptions.length !== 0 && !props.dualScreen?.presentationConnection?.id) {
		props.prescriptions.forEach((prescri) => {
			if (prescri && prescri.scans) prescri.scans.forEach((scan) => scanList.push(scan));
		});
	}

	const ConnectedEditPatientForm = withTranslation()(connect(mapStateToProps, mapDispatchToProps)(EditPatientForm));

	return (
		<div style={{ width: "100%", height: "100%", display: "flex" }}>
			{!props.dualScreen?.presentationConnection?.id && !isMobile() && scanList.length > 0 && (
				<FilePreview files={scanList} disabled={props.disabled} />
			)}

			<ConnectedEditPatientForm {...props} />
		</div>
	);
};

class EditPatientForm extends React.Component {
	constructor(props) {
		super(props);

		this.state = {
			height: 0,
			hasCameraAccess: false,
			insuranceReaderModal: false,
			insuranceScanId: null,
			openIgnoreCnsError: false,
			setFieldValue: null,
			mpiPatient: null,
			doctorLoading: false,
			postalCodeLoading: false,
			insuranceLoading: false,
			mpiSelection: {},
			granteeLoading: false,
			openGranteePatientModal: false,
			granteeSelected: null,
			foundGrantee: null,
			values: null,
			updateIdentitoVigilanceData: false,
			openIdentitoVigilanceEditPatientModal: false,
			identitoVigilancePatientOld: {},
			identitoVigilancePatientNew: {},
		};

		this.homeNumberRef = React.createRef();
		this.workNumberRef = React.createRef();
		this.formBodyRef = React.createRef();
		window.addEventListener("resize", this.updateHeight);
	}

	componentDidMount() {
		this.props.getAllContactComments();
		this.props.getAllSpecialRegimes();
		//this.props.getAllProviders();
		this.updateHeight();
		this.setState({ identitoVigilancePatientOld: this.props.patient });
	}

	updateHeight = () => {
		this.setState({
			height: window.innerHeight,
		});
	};

	filterPatients = debounce((searchTerm, callback, grantees) => {
		const { generalParameters, patient } = this.props;
		const myLabIdPatient = patient && patient.externalIds ? patient.externalIds.find((e) => e.service.id === 1).external_id : 0;
		const granteesIds = grantees.map((g) => g.grantee?.externalIds?.find((ext) => ext.service.id === 1)?.external_id);
		this.props
			.searchPatientsMyLab({ multiple_search: searchTerm }, 20, 0)
			.then(() => {
				this.setState({ granteeLoading: false });
				callback(
					this.props.patients.search.list
						.filter((s) => s.id !== myLabIdPatient && !granteesIds.includes(s.id))
						.map((patient) => {
							return {
								id: patient.id,
								label: `${patient.cns !== "" ? "CNS ".concat(patient.cns) : ""}  
								${patient.last_name || patient.birth_name} ${patient.first_name} - ${displayGenderText(patient, generalParameters.data)} ${
									patient.gender === "m" ? "né" : "née"
								} le ${moment(patient.birth_date).format(process.env.REACT_APP_DATE_DISPLAY_FORMAT)} - ${moment().diff(
									patient.birth_date,
									"years"
								)} ans ${patient.mobile_phone || patient.email ? "(" : ""} ${patient.mobile_phone || ""} ${patient.email || ""}${
									patient.mobile_phone || patient.email ? ")" : ""
								}`,
								...patient,
							};
						})
				);
			})
			.catch((error) => {
				this.setState({ granteeLoading: false });
				callback(error);
			});
	}, 500);

	openGranteePatientModal = (grantee, setFieldValue, values) => {
		//console.log("openGranteePatientModal need to convert from mylab ? ", grantee);
		this.setState({
			openGranteePatientModal: true,
			granteeSelected: grantee, //? convertMyLabToPatient(grantee) : null,
			setFieldValue: setFieldValue,
			values: values,
		});
	};
	closeGranteePatientModal = () => {
		this.setState({ openGranteePatientModal: false, granteeSelected: null });
	};
	searchGranteeByMatricule = (cns) => {
		this.props.getMpiPatientByMatricule(cns).then(async (resMpi) => {
			if (resMpi.payload && resMpi.payload.data && resMpi.payload.data.length > 0) {
				this.setState({ foundGrantee: resMpi.payload.data[0] });
			} else toast.warn("Ce patient n'est pas dans l'annuaire national des patients.");
		});
	};

	initFoundGrantee = () => {
		this.setState({ foundGrantee: null });
	};

	removeGrantee = (grant, values, setFieldValue) => {
		setFieldValue(
			"grantees",
			values.grantees.filter(
				(g) =>
					!(
						g.grantee.cns === grant.grantee.cns &&
						g.grantee.first_name === grant.grantee.first_name &&
						g.grantee.birth_name === grant.grantee.birth_name
					) || g.grantee.id !== grant.grantee.id
			)
		);
	};

	validateGrantee = (grantee, relation) => {
		let copy = this.state.values.grantees.slice();
		if (
			!!copy.find((g) => g.grantee.cns === grantee.cns && g.grantee.first_name === grantee.first_name && g.grantee.birth_name === grantee.birth_name)
		)
			copy = copy.map((g) => {
				if (g.grantee.cns === grantee.cns && g.grantee.first_name === grantee.first_name && g.grantee.birth_name === grantee.birth_name)
					return { id: g.id, grantee: grantee, relation: relation };
				return g;
			});
		else copy.push({ grantee: grantee, relation: relation });
		this.state.setFieldValue("grantees", copy);
	};

	updateOrCreateLocalPatient = async (myLabPatient) => {
		await this.props.getMatchingPatient(ServiceEnum.mylab, myLabPatient.id ?? 0, convertForPatientMatcher(myLabPatient));

		const { exists } = this.props.patients;
		myLabPatient = { ...myLabPatient, externalIds: exists?.externalIds };
		let localPatient = convertMyLabToPatient(myLabPatient);
		if (!exists) {
			localPatient.id = null;
			const postPatientResponse = await this.props.postPatient(localPatient);
			localPatient.id = postPatientResponse.payload.data.id;
		} else {
			localPatient.id = exists.id;
			if (!moment(localPatient.updated_at).isBefore(moment(exists.updated_at))) {
				await this.props.patchPatient(localPatient, true);
			} else {
				localPatient = { ...localPatient, ...exists };
			}
		}
		return localPatient;
	};

	activateComment = (type, setFieldValue) => {
		setFieldValue(type, true);
	};
	disactivateComment = (type, typeData, setFieldValue) => {
		setFieldValue(type, false);
		setFieldValue(typeData, "");
	};

	filterDoctors = debounce((searchTerm, callback) => {
		this.props
			.searchDoctors({ multiple_search: searchTerm }, 30, 0)
			.then(() => {
				this.setState({ doctorLoading: false });
				callback(
					this.props.doctors.list.map((doctor) => {
						return {
							value: doctor.id,
							label: `${doctor.code} - ${doctor.last_name} ${doctor.first_name} - ${doctor.speciality} - ${doctor.address.street} ${doctor.address.postal_code} ${doctor.address.city}`,
							labels: [
								{ text: doctor.code, width: 10 },
								{ text: doctor.last_name, width: 20 },
								{ text: doctor.first_name, width: 20 },
								{ text: doctor.speciality, width: 10 },
								{ text: doctor.address?.street, width: 20 },
								{ text: doctor.address?.postal_code, width: 5 },
								{ text: doctor.address?.city, width: 15 },
							],
							...doctor,
							sil_id: doctor.external_id,
							mylab_id: doctor.id,
						};
					})
				);
			})
			.catch((error) => {
				this.setState({ doctorLoading: false });
				callback(error);
			});
	}, 500);

	filterPostalCodes = debounce((searchTerm, country, callback) => {
		this.props
			.searchPostalCodes({ code: searchTerm, country: country }, 10, 0)
			.then(() => {
				const postCodes = this.props.postalCodes.list.map((postalCode) => {
					return postalCode.cities.map((city) => {
						const defaultValue = {
							value: postalCode.code,
							label: `${postalCode.code} - ${city.name}`,
							country: postalCode.country_iso,
							city: city,
							id: postalCode.id,
						};
						if (postalCode.streets.length > 0) {
							return postalCode.streets.map((street) => {
								if (street.displayWithPostalCode)
									return {
										value: postalCode.code,
										label: `${street.name} - ${postalCode.code} - ${city.name}`,
										country: postalCode.country_iso,
										city: city,
										street: street,
										id: postalCode.id,
									};
								return defaultValue;
							});
						}
						return defaultValue;
					});
				});
				const mergedPostCodes = [].concat.apply([], [].concat.apply([], postCodes));
				this.setState({ postalCodeLoading: false });
				callback(mergedPostCodes.sort((a, b) => (a.label < b.label ? -1 : a.label > b.label ? 1 : 0)));
			})
			.catch((error) => {
				this.setState({ postalCodeLoading: false });
				callback(error);
			});
	}, 500);

	filterInsurances = debounce((searchTerm, callback) => {
		this.props
			.searchInsurances({ name: searchTerm }, 20, 0)
			.then(() => {
				this.setState({ insuranceLoading: false });
				callback(
					this.props.insurances.list.map((insurance) => {
						return {
							id: insurance.id,
							value: insurance.id,
							label: insurance.name,
						};
					})
				);
			})
			.catch((error) => {
				this.setState({ insuranceLoading: false });
				callback(error);
			});
	}, 500);

	autofillDataPatient = async (cns, setFieldValue) => {
		await this.props.getMpiPatientByMatricule(cns).then(async (res) => {
			const results = res.payload.data;
			const { mpiSelection } = this.state;
			if (results && !results.error && results.filter((p) => p.matricule === cns.toString()).length >= 1) {
				const patient = results.filter((p) => p.matricule === cns.toString())[0];
				this.setState({
					mpiPatient: { ...patient, cns: patient.matricule },
					setFieldValue: setFieldValue,
					mpiSelection: {
						gender: mpiSelection.gender ?? !!patient.gender,
						birth_name: mpiSelection.birth_name ?? !!patient.birth_name,
						marital_name: mpiSelection.marital_name ?? !!patient.marital_name,
						first_name: mpiSelection.first_name ?? !!patient.first_name,
						birth_date: mpiSelection.birth_date ?? !!patient.birth_date,
						phone: mpiSelection.phone ?? !!patient.phone,
						mobile_phone: mpiSelection.mobile_phone ?? !!patient.mobile_phone,
						email: mpiSelection.email ?? !!patient.email,
						address: mpiSelection.address ?? !!patient.address,
					},
				});
			} else {
				if (results.error) toast.warn(results.error);
				else toast.warn("Ce patient n'est pas dans l'annuaire national des patients.");
			}
		});
	};

	onChangeMpiSelection = (prop) => {
		let { mpiSelection } = this.state;
		mpiSelection[prop] = !mpiSelection[prop];
		this.setState({ mpiSelection: mpiSelection });
	};

	cancelAutofillFromMpi = () => {
		this.setState({
			mpiPatient: null,
			mpiSelection: {},
		});
	};

	confirmAutofillFromMpi = () => {
		const { mpiPatient, setFieldValue, mpiSelection } = this.state;
		if (mpiSelection.birth_name && mpiPatient.birth_name) setFieldValue("birth_name", mpiPatient.birth_name.toUpperCase());
		if (mpiSelection.marital_name && mpiPatient.marital_name) setFieldValue("married_name", mpiPatient.marital_name.toUpperCase());
		if (mpiSelection.first_name && mpiPatient.first_name) setFieldValue("first_name", capitalize(mpiPatient.first_name));
		if (mpiSelection.birth_date && mpiPatient.birth_date) setFieldValue("birth_date", moment(mpiPatient.birth_date).format("YYYY-MM-DD"));
		if (mpiSelection.gender && mpiPatient.gender) setFieldValue("gender", mpiPatient.gender.toLowerCase());
		if (mpiSelection.phone && mpiPatient.phone) setFieldValue("phone", mpiPatient.phone);
		if (mpiSelection.mobile_phone && mpiPatient.mobile_phone && mpiPatient.mobile_phone.length > 0) {
			setFieldValue("hasMobilePhone", true);
			setFieldValue("mobile_phone", mpiPatient.mobile_phone);
		}
		if (mpiSelection.email && mpiPatient.email && mpiPatient.email.length > 0) {
			setFieldValue("email", mpiPatient.email);
			setFieldValue("hasEmail", true);
		}
		if (mpiSelection.address && mpiPatient.address) {
			setFieldValue("hasHomeAddress", true);
			setFieldValue("home_number", findNumberInAddress(mpiPatient.address.street));
			setFieldValue("home_street", removeNumberFromStreet(findNumberInAddress(mpiPatient.address.street), mpiPatient.address.street));
			setFieldValue("home_postal_code", mpiPatient.address.postal_code);
			setFieldValue("home_city", mpiPatient.address.city);
			setFieldValue("home_country", countries.alpha3ToAlpha2(mpiPatient.address.country));
		}

		this.setState({
			mpiPatient: null,
		});
	};

	openInsuranceReaderModal = (index) => {
		const scanAvailableState = {
			hasCameraAccess: true,
			insuranceReaderModal: true,
			insuranceScanId: index,
		};
		this.setState(scanAvailableState);
	};

	closeInsuranceReaderModal = () => {
		this.setState({
			insuranceReaderModal: false,
		});
	};

	submitInsuranceReaderModal = (value, setFieldValue) => {
		setFieldValue(`patientInsurances.${this.state.insuranceScanId}.number`, value);
		this.closeInsuranceReaderModal();
	};

	openIgnoreCnsErrorConfirm = () => this.setState({ openIgnoreCnsError: true });

	confirmIgnoreCnsErrorConfirm = (setFieldValue) => {
		setFieldValue("ignoreCnsError", true);
		this.setState({ openIgnoreCnsError: false });
	};

	cancelIgnoreCnsErrorConfirm = () => this.setState({ openIgnoreCnsError: false });

	isSpecialRegimeCee = (specialRegimeId) => {
		const { specialRegimes } = this.props;
		const specialRegime = specialRegimes.list.find((sr) => sr.id === specialRegimeId);
		return specialRegime?.geslab_id === process.env.REACT_APP_DEFAULT_GESLAB_CEE_ID;
	};

	enableIdentitoVigilanceUpdate = () => this.setState({ updateIdentitoVigilanceData: true });

	openIdentitoVigilanceEditPatientModal = (patient) => {
		this.setState({ openIdentitoVigilanceEditPatientModal: true, identitoVigilancePatientNew: patient });
	};

	closeIdentitoVigilanceEditPatientModal = () => {
		this.setState({ openIdentitoVigilanceEditPatientModal: false, identitoVigilancePatientNew: {} });
	};

	submitIdentitoVigilanceEditPatientModal = () => {
		this.props.onSubmit(this.state.identitoVigilancePatientNew);
		this.setState({ openIdentitoVigilanceEditPatientModal: false, identitoVigilancePatientNew: {} });
		this.props.hideSidebar();
	};

	isSameSensibleData = (oldPatient, newPatient) => {
		return (
			oldPatient.first_name === newPatient.first_name &&
			oldPatient.birth_name === newPatient.birth_name &&
			oldPatient.birth_date === newPatient.birth_date &&
			oldPatient.cns === newPatient.cns &&
			oldPatient.gender === newPatient.gender &&
			((!oldPatient.email && !newPatient.email) || oldPatient.email === newPatient.email) &&
			((!oldPatient.mobile_phone && !newPatient.mobile_phone) || isSamePhoneNumber(oldPatient.mobile_phone, newPatient.mobile_phone))
		);
	};

	render() {
		const {
			hideSidebar,
			onSubmit,
			patient,
			specialRegimes,
			cities,
			streets,
			patients,
			disabled,
			contactComments,
			// condensedDisplay = false,
			hideOnSubmit = true,
			t,
		} = this.props;
		const { mpiPatient, mpiSelection, updateIdentitoVigilanceData } = this.state;
		return (
			<div
				style={{
					backgroundColor: "rgb(236, 240, 244)",
					padding: "2rem",
				}}
			>
				<Grid>
					<SidebarTitle title={disabled ? "Les données du patient" : "Modifier un patient"} />

					<Grid.Row columns={1}>
						<Grid.Column style={{ padding: 0 }}>
							<Formik
								initialValues={{
									id: patient.id,
									gender: patient.gender || "",
									married_name: patient.married_name || "",
									first_name: patient.first_name || "",
									birth_date: patient.birth_date ? moment(patient.birth_date).format("YYYY-MM-DD") : "",
									birth_name: patient.birth_name || "",
									ignoreCnsError: false,
									cns: patient.cns || "",
									hasMobileComment: !!patient.mobile_phone_comment || false,
									hasEmailComment: !!patient.email_comment || false,
									hasFixeComment: !!patient.phone_comment || false,
									patientInsurances: [
										{
											id:
												patient.patientInsurances && patient.patientInsurances.length > 0 && patient.patientInsurances[0]
													? patient.patientInsurances[0].id
													: null,
											insurance:
												patient.patientInsurances && patient.patientInsurances.length > 0 && patient.patientInsurances[0]
													? {
															id: patient.patientInsurances[0].insurance.id,
															value: patient.patientInsurances[0].insurance.id,
															label: patient.patientInsurances[0].insurance.name,
													  }
													: null,
											number:
												patient.patientInsurances && patient.patientInsurances.length > 0 && patient.patientInsurances[0]
													? patient.patientInsurances[0].number
													: "",
										},
										{
											id:
												patient.patientInsurances && patient.patientInsurances.length > 1 && patient.patientInsurances[1]
													? patient.patientInsurances[1].id
													: null,
											insurance:
												patient.patientInsurances && patient.patientInsurances.length > 1 && patient.patientInsurances[1]
													? {
															id: patient.patientInsurances[1].insurance.id,
															value: patient.patientInsurances[1].insurance.id,
															label: patient.patientInsurances[1].insurance.name,
													  }
													: null,
											number:
												patient.patientInsurances && patient.patientInsurances.length > 1 && patient.patientInsurances[1]
													? patient.patientInsurances[1].number
													: "",
										},
									],
									hasSecondInsurance: patient.patientInsurances[1] ? true : false,
									hasFirstInsurance: patient.patientInsurances[0] ? true : false,
									hasDoctor: !!patient.doctor,
									phone: cleanPhoneNumber(patient.phone) || "",
									phone_comment: patient.phone_comment || "",
									mobile_phone: checkPrefixePhoneNumer(patient.mobile_phone)
										? cleanPhoneNumber(patient.mobile_phone) || ""
										: formatPhoneNumberToInternational(patient.mobile_phone),
									mobile_phone_error: !checkPrefixePhoneNumer(patient.mobile_phone) ? patient.mobile_phone || "" : "",
									mobile_phone_comment: patient.mobile_phone_comment || "",
									mobile_phone_is_relative: patient.mobile_phone_is_relative || false,
									hasMobilePhone: patient.mobile_phone ? true : false,
									email: patient.email || "",
									email_comment: patient.email_comment || "",
									hasEmail: patient.email ? true : false,

									hasHomeAddress: patient.home_street ? true : false,
									home_number: patient.home_number || "",
									home_street: patient.home_street || "",
									home_complement: patient.home_complement || "",
									home_country: patient.home_country || "",
									home_city: patient.home_city || "",
									home_postal_code: patient.home_postal_code || "",
									home_postal_code_creation: false,

									hasWorkAddress: patient.work_street ? true : false,
									work_number: patient.work_number || "",
									work_street: patient.work_street || "",
									work_complement: patient.work_complement || "",
									work_country: patient.work_country || "",
									work_city: patient.work_city || "",
									work_postal_code: patient.work_postal_code || "",
									work_company: patient.work_company || "",

									specialRegime: {
										id:
											patient.specialRegime?.id ??
											specialRegimes.list?.find((regime) => regime.geslab_id === process.env.REACT_APP_DEFAULT_SPECIAL_REGIME)?.id,
									},
									provider_id: patient.provider_id || null,
									doctor: patient.doctor
										? {
												id: patient.doctor.id,
												value: patient.doctor.mylab_id,
												label: `${patient.doctor.code || ""} ${patient.doctor.last_name || ""} ${patient.doctor.first_name || ""} - ${
													patient.doctor.specialty?.name || ""
												} - ${patient.doctor.number || ""} ${patient.doctor.street || ""} ${patient.doctor.postal_code || ""} ${
													patient.doctor.city || ""
												}`,
										  }
										: null,
									languages: patient.languages
										? patient.languages.map((patientLang) => {
												const code = patientLang.language;
												const name = langs.getName(code, "fr");
												return {
													value: code,
													label: `${code} - ${name}`,
													language: code,
													id: patientLang.id,
												};
										  })
										: [],
									grantees: patient.grantees || [],
								}}
								validationSchema={schemaValidation(t)}
								onSubmit={(values) => {
									if (!this.isSameSensibleData(patient, values)) this.openIdentitoVigilanceEditPatientModal(values);
									else {
										if (hideOnSubmit) hideSidebar();
										onSubmit(values);
									}
								}}
							>
								{({ setFieldValue, errors, touched, values, handleSubmit, handleChange }) => {
									const errorItems = Object.keys(errors)
										.map((key) => {
											if (touched[key]) return errors[key];
											return null;
										})
										.filter((notNull) => notNull);

									const isSpecialRegimeCee = this.isSpecialRegimeCee(values.specialRegime.id);

									if (isSpecialRegimeCee && !values.ignoreCnsError) {
										setFieldValue("ignoreCnsError", true);
									}

									return (
										<Form>
											<div
												style={{
													overflowY: "auto",
													height: this.state.height - 120,
													marginTop: -15,
													marginBottom: 5,
													width: "100%",
												}}
												ref={this.formBodyRef}
											>
												<Grid className="gridWithNoMarginMobile" style={{ width: "100%" }}>
													{errorItems.length > 0 && (
														<Grid.Row style={{ marginRight: 5, marginTop: 5 }}>
															<Grid.Column mobile={16} tablet={16} computer={16}>
																<Message negative>
																	<Message.Header>Erreur(s)</Message.Header>
																	<Message.List items={errorItems} />
																</Message>
															</Grid.Column>
														</Grid.Row>
													)}
													<Grid style={{ width: "100%", margin: "auto", paddingRight: 0 }}>
														<Grid.Row columns={2} divided>
															{/* 1e colonne	 */}
															<Grid.Column mobile={16} tablet={16} computer={8}>
																{!disabled && (
																	<div style={{ padding: 0, width: "100%", fontSize: "0.8em" }}>
																		{!disabled && (
																			<Message warning={!updateIdentitoVigilanceData} negative={updateIdentitoVigilanceData}>
																				{!updateIdentitoVigilanceData && (
																					<Message.Header>
																						Certaines données sensibles sont volontairement non éditables.{" "}
																						<Label
																							as="a"
																							onClick={() => {
																								this.enableIdentitoVigilanceUpdate();
																							}}
																						>
																							{" "}
																							<Icon name="lock open" />
																							Editer les données sensibles
																						</Label>
																					</Message.Header>
																				)}
																				{updateIdentitoVigilanceData && (
																					<Message.Header>
																						Soyez vigilants lorsque vous modifiez les données sensibles d'un patient, ces modifications s'appliquent
																						POUR TOUS LES AUTRES DOSSIERS DE CE PATIENT.
																					</Message.Header>
																				)}
																			</Message>
																		)}
																	</div>
																)}
																<Grid>
																	<Grid.Row style={{ ...styles.rowWithoutPadding, paddingTop: "10px", marginRight: "15px" }}>
																		<Grid.Column mobile={16} tablet={16} computer={!isSpecialRegimeCee ? 10 : 16}>
																			<Field
																				component={Select}
																				name="specialRegime.id"
																				customlabel="Régime"
																				placeholder="Sélectionner un régime"
																				options={orderedSpecialRegimes(specialRegimes.list)}
																				disabled={disabled}
																				required
																			/>
																		</Grid.Column>

																		{!isSpecialRegimeCee && (
																			<>
																				<Grid.Column mobile={13} tablet={7} computer={5} style={{ paddingRight: 0 }}>
																					<Field
																						type="text"
																						name="cns"
																						customlabel={
																							!updateIdentitoVigilanceData && !disabled ? (
																								<span>
																									N° CNS <Icon name="lock" />
																								</span>
																							) : (
																								"N° CNS"
																							)
																						}
																						placeholder=""
																						component={CustomInputTextComponent}
																						onKeyDown={(e) => {
																							if (e.key === "Enter") {
																								e.preventDefault();
																							}
																						}}
																						onKeyUp={(e) => {
																							const val = e.target.value;
																							setTimeout(() => {
																								if (val.length === 8 || val.length === 13) {
																									let birth_date = moment(val.substring(0, 8), "YYYYMMDD");
																									//If the year of birth in the CNS is older than today, the CNS number was created with year of birth +2000
																									if (birth_date.year() > moment().year()) birth_date = birth_date.add(-2000, "year");
																									if (birth_date.isValid()) setFieldValue("birth_date", birth_date.format("YYYY-MM-DD"));
																								}
																							}, 500);
																						}}
																						required
																						disabled={disabled || !this.state.updateIdentitoVigilanceData}
																					/>
																				</Grid.Column>
																				{!disabled && this.state.updateIdentitoVigilanceData && (
																					<Grid.Column
																						mobile={3}
																						tablet={1}
																						computer={1}
																						style={{
																							display: "flex",
																							justifyContent: "flex-start",
																							alignItems: "baseline",
																							paddingLeft: 0,
																							paddingTop: "26px",
																						}}
																					>
																						<Popup
																							content="Rechercher les données du patient"
																							position="top right"
																							trigger={
																								<Button
																									color="green"
																									icon={patients.loading ? <Icon loading name="spinner" /> : "search"}
																									disabled={Boolean(!values.cns || values.cns.toString().length !== 13 || errors.cns)}
																									type="button"
																									onClick={(e) => {
																										if (e !== undefined) e.preventDefault();
																										this.autofillDataPatient(values.cns, setFieldValue);
																									}}
																								/>
																							}
																						/>
																					</Grid.Column>
																				)}
																			</>
																		)}
																	</Grid.Row>
																	{((errors.cns && touched.cns) || values.ignoreCnsError) && (
																		<Grid.Row style={{ ...styles.rowWithoutPadding, paddingRight: "10px" }}>
																			<Grid.Column mobile={16} tablet={16} computer={10}></Grid.Column>
																			<Grid.Column mobile={16} tablet={16} computer={6} style={{ marginTop: -10 }}>
																				<Field
																					customlabel="Ignorer l'erreur du numéro CNS "
																					labelStyle={{ color: "red" }}
																					component={SpecialButtons.Checkbox}
																					name="ignoreCnsError"
																					id="ignoreCnsError"
																					disabled={disabled}
																					onChange={(e) => {
																						if (!values.ignoreCnsError) this.openIgnoreCnsErrorConfirm();
																						else handleChange(e);
																					}}
																				/>
																			</Grid.Column>
																		</Grid.Row>
																	)}
																	<Grid.Row style={styles.rowWithoutPadding}>
																		<Grid.Column mobile={16} tablet={8} computer={5}>
																			<RadioButtons
																				name="gender"
																				field1={{ id: "m", customlabel: "Homme" }}
																				field2={{ id: "f", customlabel: "Femme" }}
																				customlabel={
																					!updateIdentitoVigilanceData && !disabled ? (
																						<span>
																							Genre <Icon name="lock" />
																						</span>
																					) : (
																						"Genre"
																					)
																				}
																				errors={errors}
																				touched={touched}
																				required
																				disabled={disabled || !this.state.updateIdentitoVigilanceData}
																			/>
																		</Grid.Column>
																		<Grid.Column mobile={16} tablet={8} computer={6}>
																			<Field
																				name="birth_date"
																				customlabel={
																					!updateIdentitoVigilanceData && !disabled ? (
																						<span>
																							Date de naissance <Icon name="lock" />
																						</span>
																					) : (
																						"Date de naissance"
																					)
																				}
																				max={moment().toDate()}
																				component={CustomDatePickerComponent}
																				required
																				disabled={disabled || !this.state.updateIdentitoVigilanceData}
																			/>
																		</Grid.Column>
																	</Grid.Row>
																	<Grid.Row style={styles.rowWithoutPadding}>
																		<Grid.Column mobile={16} tablet={8} computer={5}>
																			<Field
																				type="text"
																				name="birth_name"
																				customlabel={
																					!updateIdentitoVigilanceData && !disabled ? (
																						<span>
																							Nom de naissance <Icon name="lock" />
																						</span>
																					) : (
																						"Nom de naissance"
																					)
																				}
																				placeholder=""
																				component={CustomInputTextComponent}
																				required
																				disabled={disabled || !this.state.updateIdentitoVigilanceData}
																				forceUpperCase
																				onKeyDown={(e) => {
																					if (e.key === "Enter") {
																						e.preventDefault();
																					}
																				}}
																			/>
																		</Grid.Column>
																		<Grid.Column mobile={16} tablet={8} computer={6}>
																			<Field
																				type="text"
																				name="married_name"
																				customlabel="Nom marital"
																				placeholder=""
																				component={CustomInputTextComponent}
																				disabled={disabled}
																				forceUpperCase
																				onKeyDown={(e) => {
																					if (e.key === "Enter") {
																						e.preventDefault();
																					}
																				}}
																			/>
																		</Grid.Column>

																		<Grid.Column mobile={16} tablet={8} computer={5}>
																			<Field
																				type="text"
																				name="first_name"
																				customlabel={
																					!updateIdentitoVigilanceData && !disabled ? (
																						<span>
																							Prénom(s) <Icon name="lock" />
																						</span>
																					) : (
																						"Prénom(s)"
																					)
																				}
																				placeholder=""
																				component={CustomInputTextComponent}
																				required
																				disabled={disabled || !this.state.updateIdentitoVigilanceData}
																				forceCapitalize
																				onKeyDown={(e) => {
																					if (e.key === "Enter") {
																						e.preventDefault();
																					}
																				}}
																			/>
																		</Grid.Column>
																	</Grid.Row>
																	<Grid.Row style={styles.rowWithoutPadding}>
																		<Grid.Column mobile={16} tablet={8} computer={5}>
																			<Field
																				component={InputPhone}
																				name="mobile_phone"
																				id="mobile_phone"
																				disabled={disabled || !this.state.updateIdentitoVigilanceData}
																				customlabel={
																					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
																						<span>Téléphone mobile {!updateIdentitoVigilanceData && !disabled && <Icon name="lock" />}</span>
																						{!values.hasMobileComment && !disabled && (
																							<Popup
																								content="Ajouter un commentaire"
																								position="top right"
																								trigger={
																									<Button
																										style={{ padding: "3px" }}
																										size="mini"
																										icon="edit outline"
																										onClick={(e) => {
																											if (e !== undefined) e.preventDefault();
																											this.activateComment("hasMobileComment", setFieldValue);
																										}}
																									/>
																								}
																							/>
																						)}
																					</div>
																				}
																			/>
																			{values.mobile_phone_is_relative && (
																				<Field
																					customlabel="Téléphone d'un proche"
																					component={SpecialButtons.Checkbox}
																					name="mobile_phone_is_relative"
																					id="mobile_phone_is_relative"
																					disabled={disabled}
																				/>
																			)}
																			{values.hasMobileComment && (
																				<Field
																					component={Select}
																					isCreatable
																					name="mobile_phone_comment"
																					customlabel={
																						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
																							Commentaire téléphone mobile
																							{values.hasMobileComment && !disabled && (
																								<Popup
																									content="Supprimer le commentaire"
																									position="top right"
																									trigger={
																										<Button
																											style={{ padding: "3px" }}
																											size="mini"
																											icon="close"
																											onClick={(e) => {
																												if (e !== undefined) e.preventDefault();
																												this.disactivateComment("hasMobileComment", "mobile_phone_comment", setFieldValue);
																											}}
																										/>
																									}
																								/>
																							)}
																						</div>
																					}
																					options={contactComments.list.map((reason) => {
																						return {
																							value: reason.label,
																							label: reason.label,
																						};
																					})}
																					disabled={disabled}
																				/>
																			)}
																		</Grid.Column>

																		<Grid.Column mobile={16} tablet={8} computer={6}>
																			<Field
																				type="text"
																				name="email"
																				placeholder=""
																				onKeyDown={(e) => {
																					if (e.key === "Enter") {
																						e.preventDefault();
																					}
																				}}
																				customlabel={
																					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
																						<span>Adresse email {!updateIdentitoVigilanceData && !disabled && <Icon name="lock" />}</span>
																						{!values.hasEmailComment && !disabled && !updateIdentitoVigilanceData && (
																							<Popup
																								content="Ajouter un commentaire"
																								position="top right"
																								trigger={
																									<Button
																										style={{ padding: "3px" }}
																										size="mini"
																										icon="edit outline"
																										onClick={(e) => {
																											if (e !== undefined) e.preventDefault();
																											this.activateComment("hasEmailComment", setFieldValue);
																										}}
																									/>
																								}
																							/>
																						)}
																					</div>
																				}
																				component={CustomInputTextComponent}
																				disabled={disabled || !this.state.updateIdentitoVigilanceData}
																			/>

																			{values.hasEmailComment && (
																				<Field
																					component={Select}
																					isCreatable
																					name="email_comment"
																					customlabel={
																						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
																							Commentaire e-mail
																							{values.hasEmailComment && !disabled && (
																								<Popup
																									content="Supprimer le commentaire"
																									position="top right"
																									trigger={
																										<Button
																											style={{ padding: "3px" }}
																											size="mini"
																											icon="close"
																											onClick={(e) => {
																												if (e !== undefined) e.preventDefault();
																												this.disactivateComment("hasEmailComment", "email_comment", setFieldValue);
																											}}
																										/>
																									}
																								/>
																							)}
																						</div>
																					}
																					options={contactComments.list.map((reason) => {
																						return {
																							value: reason.label,
																							label: reason.label,
																						};
																					})}
																					disabled={disabled}
																				/>
																			)}
																		</Grid.Column>
																		<Grid.Column mobile={16} tablet={8} computer={5}>
																			<Field
																				component={InputPhone}
																				name="phone"
																				id="phone"
																				disabled={disabled}
																				customlabel={
																					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
																						Téléphone fixe
																						{!values.hasFixeComment && !disabled && (
																							<Popup
																								content="Ajouter un commentaire"
																								position="top right"
																								trigger={
																									<Button
																										style={{ padding: "3px" }}
																										size="mini"
																										icon="edit outline"
																										onClick={(e) => {
																											if (e !== undefined) e.preventDefault();
																											this.activateComment("hasFixeComment", setFieldValue);
																										}}
																									/>
																								}
																							/>
																						)}
																					</div>
																				}
																			/>
																			{values.hasFixeComment && (
																				<Field
																					component={Select}
																					isCreatable
																					name="phone_comment"
																					customlabel={
																						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
																							Commentaire téléphone fixe
																							{values.hasFixeComment && !disabled && (
																								<Popup
																									content="Supprimer le commentaire"
																									position="top right"
																									trigger={
																										<Button
																											style={{ padding: "3px" }}
																											size="mini"
																											icon="close"
																											onClick={(e) => {
																												if (e !== undefined) e.preventDefault();
																												this.disactivateComment("hasFixeComment", "phone_comment", setFieldValue);
																											}}
																										/>
																									}
																								/>
																							)}
																						</div>
																					}
																					disabled={disabled}
																					options={contactComments.list.map((reason) => {
																						return {
																							value: reason.label,
																							label: reason.label,
																						};
																					})}
																				/>
																			)}
																		</Grid.Column>
																	</Grid.Row>
																	<Grid.Row style={styles.rowWithoutPadding}>
																		<Grid.Column mobile={16} tablet={16} computer={4}>
																			<Field
																				customlabel="Domicile"
																				component={SpecialButtons.Checkbox}
																				name="hasHomeAddress"
																				id="hasHomeAddress"
																				disabled={disabled}
																				onChange={(e) => {
																					handleChange(e);
																					if (values.hasHomeAddress) {
																						setFieldValue("home_country", "");
																						setFieldValue("home_postal_code", "");
																						setFieldValue("home_city", "");
																						setFieldValue("home_street", "");
																						setFieldValue("home_number", "");
																					}
																				}}
																			/>
																		</Grid.Column>
																		<Grid.Column mobile={16} tablet={16} computer={12}></Grid.Column>
																		{values.hasHomeAddress && (
																			<>
																				<Grid.Column mobile={16} tablet={16} computer={16}>
																					<Field
																						component={Select}
																						name="home_country"
																						customlabel="Pays"
																						placeholder="Sélectionner un pays"
																						options={countriesOptions()}
																						required
																						disabled={disabled}
																					/>
																				</Grid.Column>

																				<Grid.Column mobile={16} tablet={8} computer={8}>
																					<Field
																						defaultValue={{
																							value: values.home_postal_code,
																							label: values.home_postal_code,
																						}}
																						component={AsyncFilterSelect}
																						name="home_postal_code"
																						customlabel="Code Postal"
																						promiseOptions={(code, callback) => {
																							this.setState({
																								postalCodeLoading: true,
																							});
																							return this.filterPostalCodes(code, values.home_country, callback);
																						}}
																						isLoading={this.state.postalCodeLoading}
																						customOnChange={async (option) => {
																							if (values.home_country === "") {
																								setFieldValue("home_country", option.country);
																							}
																							setFieldValue("home_postal_code", option.value);
																							setFieldValue("home_postal_code_creation", false);
																							this.props.getCitiesByPostalCode(option.id).then(() => {
																								setFieldValue("home_city", option.city.name);

																								if (option.city.name) this.homeNumberRef.current.focus();
																							});

																							this.props.getStreetsByPostalCode(option.id).then(() => {
																								setFieldValue("home_street", option.street?.name || "");
																							});
																						}}
																						isCreatable
																						onCreateClick={(val) => {
																							setFieldValue("home_postal_code", val);
																							setFieldValue("home_postal_code_creation", true);
																							resetCities();
																						}}
																						required
																						disabled={disabled}
																					/>
																				</Grid.Column>

																				<Grid.Column mobile={16} tablet={8} computer={8}>
																					<Field
																						component={Select}
																						name="home_city"
																						customlabel="Ville"
																						options={
																							values.home_postal_code && !values.home_postal_code_creation
																								? cities.list.map((city) => {
																										return {
																											value: city.name,
																											label: city.name,
																										};
																								  })
																								: []
																						}
																						isCreatable
																						required
																						disabled={disabled}
																					/>
																				</Grid.Column>

																				<Grid.Column mobile={16} tablet={4} computer={4}>
																					<Field
																						innerref={this.homeNumberRef}
																						type="text"
																						name="home_number"
																						customlabel="N°"
																						placeholder=""
																						component={CustomInputTextComponent}
																						disabled={disabled}
																					/>
																				</Grid.Column>

																				<Grid.Column mobile={16} tablet={12} computer={12}>
																					<Field
																						component={Select}
																						name="home_street"
																						customlabel="Rue"
																						options={
																							values.home_postal_code
																								? streets.list.map((street) => {
																										return {
																											value: street.name,
																											label: street.name,
																										};
																								  })
																								: []
																						}
																						isCreatable
																						required
																						disabled={disabled}
																					/>
																				</Grid.Column>
																				<Grid.Column mobile={16} tablet={16} computer={16}>
																					<Field
																						type="text"
																						name="home_complement"
																						customlabel="Complément"
																						placeholder="Batiment, étage, porte"
																						component={CustomInputTextComponent}
																						disabled={disabled}
																					/>
																				</Grid.Column>
																			</>
																		)}
																	</Grid.Row>
																</Grid>
															</Grid.Column>
															{/* 2e colonne	 */}
															<Grid.Column mobile={16} tablet={16} computer={8} style={{ paddingTop: "10px" }}>
																<Grid>
																	<Grid.Row>
																		<Grid.Column mobile={16} tablet={16} computer={16}>
																			<Field
																				customlabel="Médecin traitant"
																				component={SpecialButtons.Checkbox}
																				name="hasDoctor"
																				id="hasDoctor"
																				disabled={disabled}
																				onChange={(e) => {
																					handleChange(e);
																					if (values.hasDoctor) {
																						setFieldValue("doctor", null);
																					}
																				}}
																			/>
																			{values.hasDoctor && (
																				<Field
																					component={AsyncFilterSelect}
																					defaultValue={values.doctor}
																					name="doctor"
																					customlabel="Médecin traitant"
																					promiseOptions={(searchTerm, callback) => {
																						this.setState({ doctorLoading: true });
																						return this.filterDoctors(searchTerm, callback);
																					}}
																					onInputChange={doctorHandleInputChange}
																					isLoading={this.state.doctorLoading}
																					placeholder="Sélectionner un médecin"
																					disabled={disabled}
																				/>
																			)}
																		</Grid.Column>
																	</Grid.Row>
																	<>
																		<Field
																			customlabel="Mutuelle"
																			component={SpecialButtons.Checkbox}
																			name="hasFirstInsurance"
																			id="hasFirstInsurance"
																			disabled={disabled}
																			onChange={(e) => {
																				handleChange(e);
																				if (!values.hasFirstInsurance) {
																					setFieldValue("patientInsurances.0.insurance", null);
																					setFieldValue("patientInsurances.0.number", null);
																				}
																			}}
																		/>
																		{values.hasFirstInsurance && (
																			<InsuranceInput
																				promiseOptions={(searchTerm, callback) => {
																					this.setState({ insuranceLoading: true });
																					this.filterInsurances(searchTerm, callback);
																				}}
																				insuranceLoading={this.state.insuranceLoading}
																				mainFieldName="patientInsurances.0.insurance"
																				mainFieldDefaultValue={values.patientInsurances[0].insurance}
																				numberFieldName="patientInsurances.0.number"
																				qrCodeButtonOnClick={(e) => {
																					if (e !== undefined) e.preventDefault();
																					this.openInsuranceReaderModal(0, setFieldValue);
																				}}
																				rowStyle={{ marginRight: 5 }}
																				disabled={disabled}
																			/>
																		)}

																		{values.hasFirstInsurance && (
																			<Grid.Row style={styles.rowWithoutPadding}>
																				<Grid.Column mobile={16} tablet={16} computer={16}>
																					<Field
																						customlabel="Seconde mutuelle"
																						component={SpecialButtons.Checkbox}
																						name="hasSecondInsurance"
																						id="hasSecondInsurance"
																						disabled={disabled}
																						onChange={(e) => {
																							handleChange(e);
																							if (values.hasSecondInsurance) {
																								setFieldValue("patientInsurances.1.insurance", null);
																								setFieldValue("patientInsurances.1.number", "");
																							}
																						}}
																					/>
																					{values.hasSecondInsurance && (
																						<InsuranceInput
																							promiseOptions={(searchTerm, callback) => {
																								this.setState({
																									insuranceLoading: true,
																								});
																								this.filterInsurances(searchTerm, callback);
																							}}
																							insuranceLoading={this.state.insuranceLoading}
																							mainFieldName="patientInsurances.1.insurance"
																							mainFieldDefaultValue={values.patientInsurances[1].insurance}
																							numberFieldName="patientInsurances.1.number"
																							qrCodeButtonOnClick={(e) => {
																								if (e !== undefined) e.preventDefault();
																								this.openInsuranceReaderModal(1, setFieldValue);
																							}}
																							rowStyle={{ marginBottom: 20 }}
																							disabled={disabled}
																						/>
																					)}
																				</Grid.Column>
																			</Grid.Row>
																		)}
																	</>
																	<Grid.Row style={styles.rowWithoutPadding}></Grid.Row>
																	<Grid.Row style={styles.rowWithoutPadding}>
																		<Grid.Column mobile={16} tablet={16} computer={16}>
																			<Field
																				component={Select}
																				name="languages"
																				customlabel="Langue"
																				isMulti
																				options={languagesOptions()}
																				placeholder="Sélectionner une langue"
																				disabled={disabled}
																			/>
																		</Grid.Column>
																	</Grid.Row>
																	<Grid.Row>
																		<Grid style={{ width: "100%", margin: "5px 15px", padding: "3px", backgroundColor: "rgba(0, 167, 148,0.08)" }}>
																			{!disabled && (
																				<Grid.Row
																					style={{
																						display: "flex",
																						marginRight: 5,
																						justifyContent: "flex-start",
																						alignItems: "flex-end",
																						padding: "3px 0px",
																					}}
																				>
																					<Grid.Column mobile={14} tablet={14} computer={14} style={{ paddingRight: 0 }}>
																						<p>Personnes ayant accès aux résultats du patient</p>
																					</Grid.Column>
																					<Grid.Column
																						mobile={2}
																						tablet={2}
																						computer={2}
																						style={{ paddingLeft: "5px", display: "flex", justifyContent: "end" }}
																					>
																						<Button
																							style={styles.button}
																							onClick={(e) => {
																								if (e !== undefined) e.preventDefault();
																								this.openGranteePatientModal(null, setFieldValue, values);
																							}}
																						>
																							<Icon name="add user" style={styles.icon} />
																						</Button>
																					</Grid.Column>
																				</Grid.Row>
																			)}

																			<Grid.Row style={{ marginRight: 5, paddingTop: 0 }}>
																				<Grid.Column>
																					{disabled && (
																						<div style={{ fontSize: "0.8em", paddingTop: "15px" }}>
																							Personnes ayant accès aux résultats du patient
																						</div>
																					)}
																					<Grid
																						style={{
																							margin: "1px 0",
																							backgroundColor: disabled ? "#ecf0f4" : "white",
																							fontSize: "0.7em",
																							minHeigth: "25px",
																							borderColor: "hsl(0,0%,80%)",
																							borderRadius: "4px",
																							borderStyle: "solid",
																							borderWidth: "1px",
																						}}
																					>
																						{values.grantees &&
																							values.grantees.length > 0 &&
																							values.grantees.map((g) => (
																								<Grid.Row columns={!isMobile() ? 3 : 2} key={g.grantee?.cns + g.relation}>
																									<Grid.Column mobile={13} tablet={6} computer={7}>
																										{g.grantee?.last_name?.toUpperCase() || g.grantee?.birth_name?.toUpperCase()}{" "}
																										{capitalize(g.grantee?.first_name)} (<b>{g.relation ?? ""}</b>)
																										{isMobile() ? (
																											<div>
																												{g.grantee?.mobile_phone ?? ""} {g.grantee?.mobile_phone && g.grantee?.email ? "   " : ""}{" "}
																												{g.grantee?.email ?? ""}
																											</div>
																										) : (
																											""
																										)}
																									</Grid.Column>
																									{!isMobile() ? (
																										<Grid.Column mobile={6} tablet={6} computer={7}>
																											{g.grantee?.mobile_phone ?? ""} {g.grantee?.mobile_phone && g.grantee?.email ? "   " : ""}{" "}
																											{g.grantee?.email ?? ""}
																										</Grid.Column>
																									) : (
																										""
																									)}
																									{!disabled && (
																										<Grid.Column mobile={3} tablet={2} computer={2} style={{ padding: "0" }}>
																											<Button
																												style={{ padding: "5px" }}
																												size="mini"
																												icon="pencil alternate"
																												onClick={(e) => {
																													if (e !== undefined) e.preventDefault();
																													this.openGranteePatientModal(g, setFieldValue, values);
																												}}
																											/>
																											<Button
																												style={{ padding: "5px" }}
																												size="mini"
																												icon="close"
																												onClick={(e) => {
																													if (e !== undefined) e.preventDefault();
																													this.removeGrantee(g, values, setFieldValue);
																												}}
																											/>
																										</Grid.Column>
																									)}
																								</Grid.Row>
																							))}
																						{values.grantees && values.grantees.length === 0 && (
																							<Grid.Row columns={1}>
																								<Grid.Column mobile={16} tablet={4} computer={16}>
																									Pas de tuteur
																								</Grid.Column>
																							</Grid.Row>
																						)}
																					</Grid>
																				</Grid.Column>
																			</Grid.Row>
																		</Grid>
																	</Grid.Row>
																	<Grid.Row style={{ marginRight: 5 }}>
																		<Grid.Column mobile={16} tablet={16} computer={16}>
																			<Field
																				customlabel="Autre adresse"
																				component={SpecialButtons.Checkbox}
																				name="hasWorkAddress"
																				id="hasWorkAddress"
																				disabled={disabled}
																				onChange={(e) => {
																					handleChange(e);
																					if (values.hasWorkAddress) {
																						setFieldValue("work_company", "");
																						setFieldValue("work_country", "");
																						setFieldValue("work_postal_code", "");
																						setFieldValue("work_city", "");
																						setFieldValue("work_street", "");
																						setFieldValue("work_number", "");
																					}
																				}}
																			/>
																		</Grid.Column>
																		{values.hasWorkAddress && (
																			<>
																				<Grid.Column mobile={16} tablet={16} computer={16}>
																					<Field
																						type="text"
																						name="work_company"
																						customlabel="Entreprise"
																						placeholder=""
																						component={CustomInputTextComponent}
																						disabled={disabled}
																					/>
																				</Grid.Column>

																				<Grid.Column mobile={16} tablet={16} computer={16}>
																					<Field
																						component={Select}
																						name="work_country"
																						customlabel="Pays"
																						placeholder="Sélectionner un pays"
																						options={countriesOptions()}
																						required
																						disabled={disabled}
																					/>
																				</Grid.Column>
																				<Grid.Column mobile={16} tablet={8} computer={8}>
																					<Field
																						defaultValue={{
																							value: values.work_postal_code,
																							label: values.work_postal_code,
																						}}
																						component={AsyncFilterSelect}
																						name="work_postal_code"
																						customlabel="Code Postal"
																						promiseOptions={(code, callback) => {
																							this.setState({
																								postalCodeLoading: true,
																							});
																							return this.filterPostalCodes(code, values.work_country, callback);
																						}}
																						isLoading={this.state.postalCodeLoading}
																						customOnChange={async (option) => {
																							if (values.work_country === "") {
																								setFieldValue("work_country", option.country);
																							}
																							setFieldValue("work_postal_code", option.value);
																							this.props.getCitiesByPostalCode(option.id, true).then(() => {
																								setFieldValue("work_city", option.city.name);

																								if (option.city.name) this.workNumberRef.current.focus();
																							});
																							this.props.getStreetsByPostalCode(option.id, true).then(() => {
																								setFieldValue("work_street", option.street?.name || "");
																							});
																						}}
																						isCreatable
																						onCreateClick={(val) => {
																							setFieldValue("work_postal_code", val);
																							resetWorkCities();
																						}}
																						required
																						disabled={disabled}
																					/>
																				</Grid.Column>
																				<Grid.Column mobile={16} tablet={8} computer={8}>
																					<Field
																						component={Select}
																						name="work_city"
																						customlabel="Ville"
																						options={
																							values.work_postal_code
																								? cities.workList.map((city) => {
																										return {
																											value: city.name,
																											label: city.name,
																										};
																								  })
																								: []
																						}
																						isCreatable
																						required
																						disabled={disabled}
																					/>
																				</Grid.Column>
																				<Grid.Column mobile={16} tablet={4} computer={4}>
																					<Field
																						innerref={this.workNumberRef}
																						type="text"
																						name="work_number"
																						customlabel="N°"
																						placeholder=""
																						component={CustomInputTextComponent}
																						disabled={disabled}
																					/>
																				</Grid.Column>
																				<Grid.Column mobile={16} tablet={12} computer={12}>
																					<Field
																						component={Select}
																						name="work_street"
																						customlabel="Rue"
																						options={
																							values.home_postal_code
																								? streets.workList.map((street) => {
																										return {
																											value: street.name,
																											label: street.name,
																										};
																								  })
																								: []
																						}
																						isCreatable
																						required
																						disabled={disabled}
																					/>
																				</Grid.Column>
																				<Grid.Column mobile={16} tablet={16} computer={16}>
																					<Field
																						type="text"
																						name="work_complement"
																						customlabel="Complément"
																						placeholder="Batiment, étage, porte"
																						component={CustomInputTextComponent}
																						disabled={disabled}
																					/>
																				</Grid.Column>
																			</>
																		)}
																	</Grid.Row>
																</Grid>
															</Grid.Column>
														</Grid.Row>
													</Grid>
												</Grid>
											</div>
											<Grid>
												<Grid.Row
													columns={3}
													style={{
														paddingTop: 10,
														marginRight: 5,
														marginLeft: 5,
													}}
												>
													{disabled && <Grid.Column></Grid.Column>}
													<Grid.Column>
														<SecondaryButton
															type="button"
															style={{ width: "100%" }}
															text={disabled ? "Retour" : "Annuler"}
															onClick={(e) => {
																if (e !== undefined) e.preventDefault();
																hideSidebar();
															}}
														/>
													</Grid.Column>
													<Grid.Column></Grid.Column>
													{!disabled && (
														<Grid.Column>
															<MainButton
																style={{ width: "100%" }}
																text="Valider"
																type="submit"
																onClick={() => {
																	handleSubmit();
																	this.formBodyRef.current.scrollTop = 0;
																}}
																disabled={disabled}
															/>
														</Grid.Column>
													)}
												</Grid.Row>
											</Grid>

											<InsuranceModal
												open={this.state.insuranceReaderModal}
												close={this.closeInsuranceReaderModal}
												submit={(value) => {
													this.submitInsuranceReaderModal(value, setFieldValue);
												}}
												hasCameraAccess={this.state.hasCameraAccess}
											/>

											<CompareMpiDataModal
												mpiPatient={mpiPatient}
												patient={patient}
												mpiSelection={mpiSelection}
												confirmAutofillFromMpi={this.confirmAutofillFromMpi}
												cancelAutofillFromMpi={this.cancelAutofillFromMpi}
												onChangeMpiSelection={this.onChangeMpiSelection}
											/>

											<Confirm
												open={this.state.openIgnoreCnsError}
												content="Êtes-vous sûr de vouloir ignorer l'erreur du numéro CNS ? Celui-ci n'est pas valide"
												cancelButton="Non"
												confirmButton="Oui"
												onCancel={this.cancelIgnoreCnsErrorConfirm}
												onConfirm={() => this.confirmIgnoreCnsErrorConfirm(setFieldValue)}
											/>
										</Form>
									);
								}}
							</Formik>
							<GranteePatientModal
								open={this.state.openGranteePatientModal}
								close={this.closeGranteePatientModal}
								grantee={this.state.granteeSelected}
								contactComments={contactComments}
								submit={this.validateGrantee}
								foundGrantee={this.state.foundGrantee}
								initFoundGrantee={this.initFoundGrantee}
								searchByMatricule={this.searchGranteeByMatricule}
								specialRegimes={specialRegimes}
							/>
							<IdentitoVigilanceEditPatientModal
								open={this.state.openIdentitoVigilanceEditPatientModal}
								close={this.closeIdentitoVigilanceEditPatientModal}
								submit={this.submitIdentitoVigilanceEditPatientModal}
								patientOld={this.state.identitoVigilancePatientOld}
								patientNew={this.state.identitoVigilancePatientNew}
							/>
						</Grid.Column>
					</Grid.Row>
				</Grid>
			</div>
		);
	}
}

const mapStateToProps = (state) => {
	return {
		insurances: state.insurances,
		specialRegimes: state.specialRegimes,
		doctors: state.doctors,
		postalCodes: state.postalCodes,
		cities: state.cities,
		streets: state.streets,
		patients: state.patients,
		contactComments: state.contactComments,
		generalParameters: state.generalParameters,
	};
};

const mapDispatchToProps = {
	searchInsurances,
	getAllSpecialRegimes,
	getAllProviders,
	searchDoctors,
	searchPostalCodes,
	getCitiesByPostalCode,
	resetCities,
	resetWorkCities,
	getStreetsByPostalCode,
	getMpiPatientByMatricule,
	getAllContactComments,
	searchPatientsMyLab,
	getMatchingPatient,
	patchPatient,
	postPatient,
};

export default EditPatient;
